import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  Spacer,
  Spinner,
  Text,
  Tooltip,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon, CheckCircleIcon } from "@chakra-ui/icons";
import { usePlacement } from "../../hooks/usePlacement";
import { usePartyNames } from "../../hooks/usePartyNames";
import { getLineRequest, getQuoteRequest, setPotentialLeads, shareSubmission, updateSubmission } from "../../services/PlacingAPI";
import { showApiErrorToast } from "../../utils/showApiToast";
import PotentialLeadsPicker, { fullLeadList } from "./PotentialLeadsPicker";
import MarketList from "./MarketList";
import NotesField from "./NotesField";
import QuoteRequestForm from "./QuoteRequestForm";
import QuoteComparison from "./QuoteComparison";
import CarrierSubmissionView from "./CarrierSubmissionView";
import CarrierQuoteRequestView from "./CarrierQuoteRequestView";
import OrderSection from "./OrderSection";
import CarrierLineRequestView from "./CarrierLineRequestView";
import { awaitingResponse, eligibleForQuoteRequest, isSignedDown, formatDate, readParticipation, requestCounts, toIsoUtc, toLocalInput } from "./placingUtils";
import PlacementCodify from "../codify/PlacementCodify";


const StageChip = ({ label, state, active, done, isDisabled }) => (
  <HStack
    spacing={1.5}
    px={3}
    py={1}
    borderRadius="full"
    borderWidth="1px"
    borderColor={active ? "teal.400" : "gray.300"}
    bg={active ? "teal.50" : "white"}
    opacity={isDisabled ? 0.5 : 1}
    fontSize="xs">
    {done ? (
      <CheckCircleIcon color="teal.500" boxSize={3} />
    ) : (
      <Box boxSize={3} borderRadius="full" borderWidth="2px" borderColor={active ? "teal.500" : "gray.400"} />
    )}
    <Text fontWeight="bold" letterSpacing="wide">{label}</Text>
    {state && <Text color="gray.600">{state}</Text>}
  </HStack>
);

const StepItem = ({ label, tag, tagColour = "blue", onClick, isDisabled, colour }) => (
  <HStack
    as="button"
    type="button"
    w="100%"
    justify="space-between"
    px={2}
    py={2}
    borderRadius="md"
    textAlign="left"
    fontSize="sm"
    color={colour}
    opacity={isDisabled ? 0.5 : 1}
    cursor={isDisabled ? "not-allowed" : "pointer"}
    _hover={isDisabled ? undefined : { bg: "gray.50" }}
    onClick={isDisabled ? undefined : onClick}>
    <Text>{label}</Text>
    {tag && <Badge colorScheme={tagColour} borderRadius="full" px={2} textTransform="none">{tag}</Badge>}
  </HStack>
);

/** Start the placement: the notes become the draft submission's notes. */
const StartStep = ({ onStart, isStarting }) => {
  const [notes, setNotes] = useState("");
  return (
    <VStack align="stretch" spacing={3}>
      <Text fontSize="sm" color="gray.600">
        Creates the placement and a draft submission from this document. You choose the leads next.
      </Text>
      <NotesField label="Notes for the market (optional)" value={notes} onChange={setNotes} />
      <HStack justify="flex-end">
        <Button size="sm" colorScheme="blue" onClick={() => onStart(notes.trim())} isLoading={isStarting}>
          Create submission
        </Button>
      </HStack>
    </VStack>
  );
};

/** Choose potential leads, then save the draft or send (also used to add leads later). */
const SubmissionStep = ({ submission, onChanged, onDone }) => {
  const toast = useToast();
  const isDraft = submission.status === "draft";
  const [chosen, setChosen] = useState(
    (submission.sharedWithParties || []).filter((l) => l.status === "selected").map((l) => l.partyId)
  );
  const [respondBy, setRespondBy] = useState(toLocalInput(submission.responseRequiredByDate));
  const [notes, setNotes] = useState(submission.notes || "");
  const [busy, setBusy] = useState(null);

  const save = async () => {
    // Notes can only change while it's a draft (sharing locks the submission)
    if (isDraft && notes !== (submission.notes || "")) await updateSubmission({ ...submission, notes });
    await setPotentialLeads(submission._id, fullLeadList(submission, chosen));
  };

  const saveDraft = async () => {
    setBusy("save");
    try {
      await save();
      toast({ title: "Potential leads saved", status: "success" });
      await onChanged();
      onDone();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't save potential leads" });
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    if (chosen.length === 0) return toast({ title: "Choose at least one potential lead", status: "warning" });
    if (!window.confirm(`Share with ${chosen.length} potential lead(s)?`)) return;
    setBusy("send");
    try {
      await save();
      await shareSubmission(submission._id, isDraft ? toIsoUtc(respondBy) : undefined);
      toast({ title: "Submission sent", status: "success" });
      await onChanged();
      onDone();
    } catch (err) {
      showApiErrorToast(toast, err, { title: "Couldn't send submission" });
      await onChanged();
    } finally {
      setBusy(null);
    }
  };

  return (
    <VStack align="stretch" spacing={3}>
      <HStack justify="space-between" fontSize="sm">
        <Text>Document</Text>
        <Badge colorScheme="green" borderRadius="full" px={2} textTransform="none">
          {submission.name || "This document"} attached
        </Badge>
      </HStack>
      <Box>
        <Text fontSize="xs" color="gray.500" mb={1}>Potential leads</Text>
        <Box maxH="220px" overflowY="auto">
          <PotentialLeadsPicker submission={submission} value={chosen} onChange={setChosen} />
        </Box>
      </Box>
      {isDraft && <NotesField label="Notes for the market" value={notes} onChange={setNotes} />}
      {isDraft && (
        <FormControl>
          <FormLabel fontSize="xs">Respond by</FormLabel>
          <Input size="sm" type="datetime-local" value={respondBy} onChange={(e) => setRespondBy(e.target.value)} />
        </FormControl>
      )}
      <HStack justify="flex-end">
        <Button size="sm" variant="outline" onClick={saveDraft} isLoading={busy === "save"} isDisabled={!!busy}>
          {isDraft ? "Save draft" : "Save"}
        </Button>
        <Button size="sm" colorScheme="blue" onClick={send} isLoading={busy === "send"} isDisabled={!!busy || chosen.length === 0}>
          {isDraft ? "Send submission" : `Send to ${chosen.length} new`}
        </Button>
      </HStack>
    </VStack>
  );
};

/**
 * The placing toolbar inside a document: stage chips, the market, and the
 * next step to take, as broker or as a carrier the submission was sent to.
 * Starting and deleting a placement stay with the editor (they need its
 * organisation and sender-party pickers); everything else happens here.
 */
const PlacingToolbar = ({
  documentId,
  refreshKey,
  onStartPlacement,
  isCreatingPlacement,
  onDeletePlacement,
  isDeletingPlacement,
}) => {
  const navigate = useNavigate();
  const { loading, error, placement, submission, role, comparison, lines, myQuoteRequests, myLineRequests, reload } =
    usePlacement({ documentId, refreshKey });
  const [stepOpen, setStepOpen] = useState(false);
  const [view, setView] = useState("menu");
  const [modal, setModal] = useState(null); // { kind, quoteRequestId? }
  const [opened, setOpened] = useState(null); // the carrier's quote or line request, fetched by id

  useEffect(() => {
    setOpened(null);
    const id = modal?.kind === "carrierQuote" ? modal.quoteRequestId : modal?.kind === "carrierLine" ? modal.lineRequestId : null;
    if (!id) return undefined;
    let cancelled = false;
    (modal.kind === "carrierQuote" ? getQuoteRequest(id) : getLineRequest(id))
      .then((record) => !cancelled && record?._id && setOpened(record))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [modal?.kind, modal?.quoteRequestId, modal?.lineRequestId]);

  const leads = submission?.sharedWithParties || [];
  // A carrier user may act for several parties: one entry (and quote request) each
  const myEntries = role === "carrier" ? leads : [];
  const names = usePartyNames([
    submission?.sender?.partyId,
    ...myEntries.map((e) => e.partyId),
    ...(myQuoteRequests || []).map((q) => q.receiver?.partyId),
  ]);

  const isShared = submission?.status === "shared";
  const rows = comparison?.rows || [];
  const nominated = rows.some((r) => r.status === "nominated");
  const quoted = comparison?.quoted ?? rows.filter((r) => r.status === "quoted").length;
  const awaiting = comparison?.awaiting ?? rows.filter((r) => r.status === "sent").length;
  const sentCount = leads.filter((l) => l.status !== "selected").length;
  // Interested leads not sent a quote request yet (any interested lead can get another)
  const sentCounts = requestCounts(rows);
  const eligibleForQuotes = eligibleForQuoteRequest(leads).filter((l) => !sentCounts[l.partyId]).length;
  const myRequest = myQuoteRequests?.[0];

  const closeStep = () => {
    setStepOpen(false);
    setView("menu");
  };
  const openModal = (kind, extra = {}) => {
    closeStep();
    setModal({ kind, ...extra });
  };

  // --- stage chips ---
  let submissionState = "not started";
  let quotationState = "";
  let onQuotation = false;
  // Order: from the first line request
  const lineRows = lines?.rows || [];
  const onOrder = role === "broker" ? lineRows.length > 0 : (myLineRequests || []).length > 0;
  const signedDown = isSignedDown(placement);
  const orderState =
    role === "broker"
      ? signedDown
        ? "signed line advice"
        : lineRows.length
        ? `${lineRows.length} requested · ${lineRows.filter((r) => r.status === "offered").length} offered`
        : ""
      : (myLineRequests || []).length
      ? `line ${myLineRequests[0].status === "sent" ? "requested" : myLineRequests[0].status}`
      : "";
  if (role === "broker") {
    if (submission?.status === "draft") submissionState = "draft";
    if (isShared) submissionState = `sent to ${sentCount} · locked`;
    // Still Submission (build) once shared; Quotation starts with the first quote request
    if (rows.length) {
      onQuotation = true;
      quotationState = nominated ? "lead nominated" : `${quoted} quoted · ${awaiting} awaiting`;
    }
  } else if (role === "carrier") {
    const declinedEntries = myEntries.filter((e) => e.status === "declined").length;
    submissionState =
      myEntries.length > 1
        ? `received${declinedEntries ? ` · ${declinedEntries} of ${myEntries.length} declined` : ""}`
        : declinedEntries
        ? "declined"
        : "received";
    if (myRequest) {
      onQuotation = true;
      quotationState =
        myQuoteRequests.length > 1
          ? `${myQuoteRequests.length} quote requests`
          : myRequest.status === "sent"
          ? "quote requested"
          : myRequest.status;
    }
  }

  // --- next steps ---
  const brokerSteps = () => {
    if (!placement) {
      return (
        <>
          <StepItem
            label="Create submission from this document"
            tag={isCreatingPlacement ? "Starting…" : "Recommended"}
            onClick={() => setView("start")}
            isDisabled={isCreatingPlacement}
          />
          <StepItem label="Attach existing files" tag="Coming later" tagColour="gray" isDisabled />
        </>
      );
    }
    if (!submission) return <Text fontSize="sm" color="gray.500" px={2}>No submission found for this placement.</Text>;
    if (!isShared) {
      return <StepItem label="Choose potential leads and send" tag="Recommended" onClick={() => setView("submission")} />;
    }
    if (nominated) {
      return (
        <>
          {signedDown ? (
            <StepItem label="View signed lines" tag="Signed down" tagColour="green" onClick={() => openModal("order")} />
          ) : (
            <StepItem
              label={lineRows.length ? "Request more lines, view lines or sign down" : "Request lines (lead and followers)"}
              tag={lineRows.length ? `${lineRows.length} requested` : "Recommended"}
              onClick={() => openModal("order")}
            />
          )}
          <StepItem label="View quotes and nomination" onClick={() => openModal("compare")} />
        </>
      );
    }
    return (
      <>
        <StepItem
          label="Request quotes"
          tag={
            eligibleForQuotes && !quoted
              ? "Recommended"
              : eligibleForQuotes
              ? `${eligibleForQuotes} not sent one`
              : eligibleForQuoteRequest(leads).length
              ? "All sent one"
              : "None interested yet"
          }
          tagColour={eligibleForQuotes || eligibleForQuoteRequest(leads).length ? "blue" : "gray"}
          onClick={() => openModal("requestQuotes")}
        />
        <StepItem
          label="Compare quotes and nominate"
          tag={quoted ? "Recommended" : undefined}
          onClick={() => openModal("compare")}
        />
        <StepItem label="Add potential leads" onClick={() => setView("submission")} />
      </>
    );
  };

  // Parties still to give their build response (declare interest or decline)
  const openWithoutRequest = myEntries.filter(
    (e) => awaitingResponse(e) && !(myQuoteRequests || []).some((q) => q.receiver?.partyId === e.partyId)
  );
  const asParty = (partyId) => (partyId && myEntries.length > 1 ? ` as ${names[partyId] || "…"}` : "");

  const carrierSteps = () => (
    <>
      {(myLineRequests || []).map((r) => (
        <StepItem
          key={r._id}
          label={`${r.status === "sent" ? "Respond to line request" : "View line request"}${asParty(r.receiver?.partyId)} (${
            r.receiver?.roleCode === "lead" ? "Lead" : "Follower"
          })`}
          tag={r.status === "sent" ? "Recommended" : r.status}
          tagColour={r.status === "offered" ? "purple" : r.status === "declined" ? "red" : "blue"}
          onClick={() => openModal("carrierLine", { lineRequestId: r._id })}
        />
      ))}
      {(myQuoteRequests || []).map((q) => (
        <StepItem
          key={q._id}
          label={`${q.status === "sent" ? "Respond to quote request" : "View quote request"}${asParty(q.receiver?.partyId)}${
            // A party can have several: tell them apart by date and share
            (myQuoteRequests || []).length > 1
              ? ` · ${formatDate(q.receiver?.sentDate || q.createdDate).split(",")[0]}${
                  readParticipation(q).requestedShare != null ? ` · ${readParticipation(q).requestedShare}%` : ""
                }`
              : ""
          }`}
          tag={q.status === "sent" ? "Recommended" : q.status}
          tagColour={q.status === "nominated" ? "green" : q.status === "ntu" ? "orange" : "blue"}
          onClick={() => openModal("carrierQuote", { quoteRequestId: q._id })}
        />
      ))}
      <StepItem
        label={myEntries.some(awaitingResponse) ? "Respond to submission (declare interest or decline)" : "View submission"}
        tag={openWithoutRequest.length ? "Recommended" : undefined}
        onClick={() => openModal("carrierSubmission")}
      />
    </>
  );

  const portalLink = placement?._id && role === "broker"
    ? `/placing/placements/${placement._id}`
    : submission?._id
    ? `/placing/submissions/${submission._id}`
    : null;

  const listedQuoteRequest = (myQuoteRequests || []).find((q) => q._id === modal?.quoteRequestId);
  const listedLineRequest = (myLineRequests || []).find((r) => r._id === modal?.lineRequestId);
  // Opened by id (as the placing pages do): that records it as viewed and starts the carrier's Codify run
  const modalQuoteRequest = opened?._id === modal?.quoteRequestId ? opened : listedQuoteRequest;
  const modalLineRequest = opened?._id === modal?.lineRequestId ? opened : listedLineRequest;
  // The message open now, for the Codify chip
  const openMessage = modal?.kind === "carrierQuote" && modal.quoteRequestId
    ? { trigger: "quote_request", triggerId: modal.quoteRequestId }
    : modal?.kind === "carrierLine" && modal.lineRequestId
    ? { trigger: "line_request", triggerId: modal.lineRequestId }
    : null;

  return (
    <Box borderBottomWidth="1px" borderColor="gray.200" px={3} py={2} bg="white">
      <HStack spacing={2} flexWrap="wrap" rowGap={2}>
        {submission && (
          <Tooltip
            label={isShared ? "The submission is locked. The document itself can still be edited." : undefined}
            hasArrow>
            <Badge colorScheme={signedDown ? "green" : isShared ? "blue" : "gray"} textTransform="none" px={2}>
              {role === "carrier" ? "Received" : signedDown ? "Signed down" : isShared ? "Shared · locked" : "Draft · editable"}
            </Badge>
          </Tooltip>
        )}

        <HStack spacing={1}>
          <StageChip label="SUBMISSION" state={loading ? "" : submissionState} active={!onQuotation && !onOrder} done={onQuotation || onOrder} />
          <ChevronRightIcon color="gray.400" />
          <StageChip label="QUOTATION" state={quotationState} active={onQuotation && !onOrder} done={nominated || onOrder} isDisabled={!onQuotation && !onOrder} />
          <ChevronRightIcon color="gray.400" />
          <StageChip label="ORDER" state={orderState} active={onOrder} isDisabled={!onOrder} />
          <ChevronRightIcon color="gray.400" />
          <StageChip label="BOUND" isDisabled />
        </HStack>

        {loading && <Spinner size="xs" />}
        {error && <Text fontSize="xs" color="red.500">{error.message || "Couldn't load placing"}</Text>}

        <Spacer />

        {role && (
          <Badge colorScheme="green" variant="subtle" fontSize="0.7em">
            {role === "broker" ? "BROKER" : "CARRIER"}
            {role === "broker"
              ? submission?.sender?.partyId && ` @ ${names[submission.sender.partyId] || "…"}`
              : myEntries.length > 0 && ` @ ${myEntries.map((e) => names[e.partyId] || "…").join(", ")}`}
          </Badge>
        )}

        {submission && (
          <Popover placement="bottom-end">
            <PopoverTrigger>
              <Button size="xs" variant="outline" rightIcon={<ChevronDownIcon />}>Market</Button>
            </PopoverTrigger>
            {/* Portal: the editor frame clips anything that overflows it */}
            <Portal>
              <PopoverContent w="360px">
                <PopoverHeader fontWeight="semibold" fontSize="sm">Market</PopoverHeader>
                <PopoverBody>
                  <MarketList submission={submission} comparison={comparison} />
                </PopoverBody>
              </PopoverContent>
            </Portal>
          </Popover>
        )}

        {/* Codify: the user's own setting and runs for this placement. The
            current message is the submission once it's shared (or received) */}
        {(placement?._id || submission?.placementId) && (
          <PlacementCodify
            placementId={placement?._id || submission?.placementId}
            message={openMessage}
            awaitMessage={openMessage || (submission?._id && (isShared || role === "carrier") ? { trigger: "submission", triggerId: submission._id } : null)}
            refreshKey={`${submission?._id || ""}:${submission?.status || ""}:${refreshKey ?? ""}:${opened?._id || ""}`}
          />
        )}

        <Tooltip label="Coming later" hasArrow>
          <Button size="xs" variant="outline" isDisabled>Thread</Button>
        </Tooltip>

        <Popover placement="bottom-end" isOpen={stepOpen} onClose={closeStep} closeOnBlur>
          <PopoverTrigger>
            <Button
              size="xs"
              colorScheme="blue"
              rightIcon={<ChevronDownIcon />}
              onClick={() => (stepOpen ? closeStep() : setStepOpen(true))}
              isDisabled={loading}>
              Next step
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent w="420px">
              <PopoverHeader fontWeight="semibold" fontSize="sm">
                <HStack justify="space-between">
                  <Text>{view === "submission" ? (isShared ? "Add potential leads" : "Create submission") : view === "start" ? "Create submission" : "Next step"}</Text>
                  {view !== "menu" && (
                    <Button size="xs" variant="ghost" onClick={() => setView("menu")}>Back</Button>
                  )}
                </HStack>
              </PopoverHeader>
              <PopoverBody>
                {view === "start" ? (
                  <StartStep
                    isStarting={isCreatingPlacement}
                    onStart={(notes) => {
                      closeStep();
                      onStartPlacement(notes);
                    }}
                  />
                ) : view === "submission" && submission ? (
                  <SubmissionStep submission={submission} onChanged={reload} onDone={closeStep} />
                ) : (
                  <VStack align="stretch" spacing={0}>
                    {role === "carrier" ? carrierSteps() : brokerSteps()}
                    {(portalLink || (role === "broker" && placement)) && <Divider my={2} />}
                    {portalLink && (
                      <StepItem label="Open in placing portal" onClick={() => navigate(portalLink)} />
                    )}
                    {role === "broker" && placement && (
                      <StepItem
                        label={isDeletingPlacement ? "Deleting…" : "Delete placement"}
                        colour="red.600"
                        isDisabled={isDeletingPlacement}
                        onClick={() => {
                          if (!window.confirm("Delete this placement?")) return;
                          closeStep();
                          onDeletePlacement();
                        }}
                      />
                    )}
                  </VStack>
                )}
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </Popover>
      </HStack>

      <Modal isOpen={!!modal} onClose={() => setModal(null)} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {{
              requestQuotes: "Request quotes",
              compare: "Compare quotes and nominate",
              carrierSubmission: "Submission",
              carrierQuote: "Quote request",
              order: "Order: request lines",
              carrierLine: "Line request",
            }[modal?.kind]}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {modal?.kind === "requestQuotes" && submission && (
              <QuoteRequestForm submission={submission} comparison={comparison} onSent={reload} />
            )}
            {modal?.kind === "compare" && submission && (
              <QuoteComparison submissionId={submission._id} placementId={submission.placementId} comparison={comparison} onChanged={reload} leads={leads} />
            )}
            {modal?.kind === "carrierSubmission" && submission && (
              <CarrierSubmissionView submission={submission} onChanged={reload} />
            )}
            {modal?.kind === "carrierQuote" && modalQuoteRequest && (
              <CarrierQuoteRequestView quoteRequest={modalQuoteRequest} onChanged={reload} />
            )}
            {modal?.kind === "order" && submission && (
              <OrderSection submission={submission} placement={placement} comparison={comparison} onChanged={reload} />
            )}
            {modal?.kind === "carrierLine" && modalLineRequest && (
              <CarrierLineRequestView lineRequest={modalLineRequest} onChanged={reload} />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PlacingToolbar;
